import dayjs from 'dayjs';

import {
  CloseDrawerButton,
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerProps,
  IconButton,
  Icons,
  MetadataChipV2,
  tokens,
  WithRef,
  Typography,
  Stack,
} from '@cast/design-system';
import {
  ClusterStatusChip,
  ClusterStatusTooltip,
  K8sProviderIcons,
  ScoreBadge,
} from '@cast/features-cluster-ui';
import {
  formatClusterDashboardMetadata,
  useClusterDashboardState,
  useClusterDashboardGaugeData,
} from '@cast/features-cluster-shared';

import { clusterDashboardChipSx } from '../../_constants/dashboardTokens';

import { ClusterDetailsDrawerRow } from './ClusterDetailsDrawerRow';

export const ClusterDetailsDrawer = ({
  ref,
  ...props
}: WithRef<HTMLDivElement, DrawerProps>) => {
  const {
    cluster,
    isManaged,
    isKentManaged,
    isTerraformManaged,
    isDemoCluster,
    hasOldK8s,
  } = useClusterDashboardState();
  const metadata = formatClusterDashboardMetadata(cluster);
  const { summary } = useClusterDashboardGaugeData();
  const clusterId = cluster.id;

  const handleCopyClusterId = async () => {
    try {
      await navigator.clipboard.writeText(clusterId);
    } catch {
      // Clipboard may be unavailable in some contexts.
    }
  };

  return (
    <Drawer ref={ref} size="small" testId="cluster-details-drawer" {...props}>
      <DrawerBody>
        <DrawerContent gap={24} py={24}>
          <Stack gap={16} width="100%">
            <Stack direction="row" alignItems="flex-start" gap={8}>
              <Stack gap={16} flex={1} minWidth={0}>
                <Stack gap={0}>
                  <Typography
                    variant="L12B"
                    sx={{ color: tokens.theme.fg.structural.secondary }}
                  >
                    cluster details:
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ color: tokens.theme.fg.structural.primary }}
                  >
                    {cluster.name || 'Cluster'}
                  </Typography>
                </Stack>
                <ClusterStatusTooltip cluster={cluster}>
                  <ClusterStatusChip
                    status={cluster.displayStatus}
                    managed={isManaged || isKentManaged}
                    size="large"
                  />
                </ClusterStatusTooltip>
              </Stack>
              <CloseDrawerButton positioned={false} sx={{ mt: -4, mr: -12 }} />
            </Stack>

            <Divider />

            <Stack gap={16}>
              <ClusterDetailsDrawerRow label="provider:">
                <Stack direction="row" alignItems="center" gap={8}>
                  <K8sProviderIcons
                    provider={metadata.provider}
                    castEnabled={isManaged}
                    size={24}
                    iconSplitter={
                      <Divider
                        orientation="vertical"
                        variant="middle"
                        flexItem
                      />
                    }
                  />
                  <Typography
                    variant="P14M"
                    sx={{ color: tokens.theme.fg.structural.primary }}
                  >
                    {metadata.providerLabel}
                  </Typography>
                </Stack>
              </ClusterDetailsDrawerRow>

              <ClusterDetailsDrawerRow label="region:">
                <Typography
                  variant="P14M"
                  sx={{ color: tokens.theme.fg.structural.primary }}
                >
                  {metadata.region}
                </Typography>
              </ClusterDetailsDrawerRow>

              <ClusterDetailsDrawerRow label="Cluster score:">
                {summary?.clusterScore ? (
                  <ScoreBadge score={summary.clusterScore} />
                ) : (
                  <Typography
                    variant="P14M"
                    sx={{ color: tokens.theme.fg.structural.primary }}
                  >
                    —
                  </Typography>
                )}
              </ClusterDetailsDrawerRow>

              <ClusterDetailsDrawerRow label="kubernetes version:">
                <Stack alignItems="flex-end" textAlign="right" gap={4}>
                  <Typography
                    variant="P14M"
                    sx={{ color: tokens.theme.fg.structural.primary }}
                  >
                    {metadata.k8sVersion}
                  </Typography>
                  {hasOldK8s ? (
                    <Typography variant="P12R" color="warning.main">
                      Outdated Kubernetes version
                    </Typography>
                  ) : null}
                </Stack>
              </ClusterDetailsDrawerRow>

              {isTerraformManaged ? (
                <ClusterDetailsDrawerRow label="onboarded via:">
                  <Typography
                    variant="P14M"
                    sx={{ color: tokens.theme.fg.structural.primary }}
                  >
                    Terraform
                  </Typography>
                </ClusterDetailsDrawerRow>
              ) : null}

              {isDemoCluster ? (
                <ClusterDetailsDrawerRow label="cluster type:">
                  <Typography
                    variant="P14M"
                    sx={{ color: tokens.theme.fg.structural.primary }}
                  >
                    Demo cluster
                  </Typography>
                </ClusterDetailsDrawerRow>
              ) : null}

              <ClusterDetailsDrawerRow label="cluster id:">
                <Stack direction="row" alignItems="center" gap={8}>
                  <IconButton
                    variant="ghost"
                    size="sm"
                    onClick={handleCopyClusterId}
                    testId="copy-cluster-id"
                  >
                    <Icons.Copy size={16} />
                  </IconButton>
                  <Typography
                    variant="P14M"
                    sx={{ color: tokens.theme.fg.structural.primary }}
                  >
                    {clusterId}
                  </Typography>
                </Stack>
              </ClusterDetailsDrawerRow>

              <ClusterDetailsDrawerRow label="connected:" alignTop>
                <Stack alignItems="flex-end" textAlign="right">
                  <Typography
                    variant="P14M"
                    sx={{ color: tokens.theme.fg.structural.primary }}
                  >
                    {metadata.connectedAt}
                  </Typography>
                  <Typography
                    variant="P14R"
                    sx={{ color: tokens.theme.fg.structural.primary }}
                  >
                    {cluster.createdAt
                      ? dayjs(cluster.createdAt).format('YYYY-MM-DD HH:mm:ss')
                      : metadata.connectedTimestamp}
                  </Typography>
                </Stack>
              </ClusterDetailsDrawerRow>
            </Stack>

            <Divider />

            <Stack gap={8} width="100%">
              <Typography
                variant="L12R"
                sx={{ color: tokens.theme.fg.structural.secondary }}
              >
                cluster tags:
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={8}>
                {metadata.tags.length ? (
                  metadata.tags.map((tag) => (
                    <MetadataChipV2
                      key={tag}
                      label={tag}
                      size="l"
                      variant="strong"
                      sx={clusterDashboardChipSx.metadataLabel}
                    />
                  ))
                ) : (
                  <Typography
                    variant="P12R"
                    sx={{ color: tokens.theme.fg.structural.secondary }}
                  >
                    No tags
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Stack>
        </DrawerContent>
      </DrawerBody>
    </Drawer>
  );
};

ClusterDetailsDrawer.displayName = 'ClusterDetailsDrawer';
